import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import moment from "moment";
import { format } from "date-fns";
import { EventProps } from "react-big-calendar";
import { toast } from "sonner";
import { AxiosError } from "axios";
import Cookies from "js-cookie";

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog/alert-dialog";
import { Button } from "@/components/ui/button/button";
import { Label } from "@/components/ui/label/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select/select";
import { getAttendanceByMonthYearForEmployee } from "@/config/queries/employee/emp-queries";
import { useAuth } from "@/lib/use-auth";
import { AttendanceEvent } from "../month-calendar/attendance-event";
import { AttendanceEventItem } from "../month-calendar/att-types";
import { HolidayEvent } from "../month-calendar/events/holiday-event";
import Calendar from "../month-calendar/Calendar";
import { Separator } from "@/components/ui/separator/separator";
import { createDateSheetClaim } from "@/config/mutation/employee/salary.mutation";

type Month = {
  label: string;
  value: number;
  disabled: boolean;
};

const AllMonths = [
  { label: "January", value: 0, disabled: false },
  { label: "February", value: 1, disabled: false },
  { label: "March", value: 2, disabled: false },
  { label: "April", value: 3, disabled: false },
  { label: "May", value: 4, disabled: false },
  { label: "June", value: 5, disabled: false },
  { label: "July", value: 6, disabled: false },
  { label: "August", value: 7, disabled: false },
  { label: "September", value: 8, disabled: false },
  { label: "October", value: 9, disabled: false },
  { label: "November", value: 10, disabled: false },
  { label: "December", value: 11, disabled: false },
] satisfies Month[];

function updateMonthsAvailability(
  months: Month[],
  selectedYear: number
): Month[] {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();

  return months.map((month) => {
    if (selectedYear === currentYear) {
      return {
        ...month,
        disabled: month.value >= currentMonth,
      };
    } else if (selectedYear < currentYear) {
      return {
        ...month,
        disabled: false,
      };
    } else {
      return {
        ...month,
        disabled: true,
      };
    }
  });
}

// const getCurrentYear = () => {
//   return new Date().getFullYear();
// };

function generateYears(): number[] {
  const currentYear = new Date().getFullYear();
  const endYear = 2024;
  const years: number[] = [];

  for (let year = currentYear; year >= endYear; year--) {
    years.push(year);
  }

  return years;
}

interface MyObject {
  _id: string;
  createdAt: string | Date; // Assuming ISO 8601 string format
  employee: string;
  mode: string;
}

function findObjectByDate(
  objects: MyObject[],
  mode: "Earliest" | "Latest"
): MyObject | null {
  if (objects.length === 0) return null;

  let resultObject: MyObject = objects[0];
  let resultDate: Date = new Date(resultObject.createdAt);

  for (let i = 1; i < objects.length; i++) {
    const currentDate: Date = new Date(objects[i].createdAt);
    if (mode === "Earliest" && currentDate < resultDate) {
      resultObject = objects[i];
      resultDate = currentDate;
    } else if (mode === "Latest" && currentDate > resultDate) {
      resultObject = objects[i];
      resultDate = currentDate;
    }
  }

  return resultObject;
}

function getMiddleDayOfMonth(year: number, month: number) {
  // Create a moment object for the first day of the given month and year
  const firstDayOfMonth = moment({ year, month: month, day: 1 });

  // Get the number of days in the month
  const daysInMonth = firstDayOfMonth.daysInMonth();

  // Calculate the middle day
  const middleDay = Math.ceil(daysInMonth / 2);

  // Create a moment object for the middle day of the month
  const middleDate = moment({ year, month: month, day: middleDay });

  // Format the date as desired (e.g., 'YYYY-MM-DD')
  return middleDate.toDate();
}

export const ClaimTimesheetModal = () => {
  // @ts-ignore
  const queryClient = useQueryClient();
  const key = Cookies.get("key") as string;
  const [selectedYear, setSelectedYear] = useState<number>(
    new Date().getFullYear()
  );

  const [selectedMonth, setSelectedMonth] = useState<number>(
    new Date().getMonth() - 1
  );

  const [date, setDate] = useState(() =>
    getMiddleDayOfMonth(selectedYear, selectedMonth)
  );

  const months = useMemo(() => {
    const result = updateMonthsAvailability(AllMonths, selectedYear);

    return result;
  }, [selectedMonth, selectedYear]);

  const years = useMemo(() => generateYears(), []);

  const { data, mutate, isPending } = useMutation({
    mutationKey: ["emp-attendance-with-monthyear"],
    mutationFn: getAttendanceByMonthYearForEmployee(key),
  });

  const { data: authData } = useAuth();

  const ATTENDANCE_EVENTS = useMemo(() => {
    if (isPending) {
      return [];
    }

    if (data === undefined || data?.attendance === undefined) {
      return [];
    }

    const result = data?.attendance?.map((att) => {
      const from = moment(att?.date)?.toDate();
      const to = moment(att?.date)?.toDate();

      // console.log("Ätt", att);

      const checkin = findObjectByDate(att?.checkin, "Earliest");
      const checkout = findObjectByDate(att?.checkout, "Latest");

      // console.log({
      //   checkin,
      //   checkout,
      // });

      return {
        start: from,
        end: to,
        data: {
          attendance: {
            id: att?._id,
            createdAt: att?.createdAt,
            status: att?.status,
            date: att?.date,
            ...(checkin !== null && checkin?.createdAt
              ? {
                  checkinTime: format(
                    new Date(checkin?.createdAt as string),
                    "PPpp"
                  ),
                }
              : {
                  checkinTime: "",
                }),
            ...(checkout !== null && checkout?.createdAt
              ? {
                  checkoutTime: format(
                    new Date(checkout?.createdAt as string),
                    "PPpp"
                  ),
                }
              : {
                  checkoutTime: "",
                }),
            // checkinTime: format(new Date(checkin?.createdAt as string), "PPpp"),
            // checkoutTime: format(
            //   new Date(checkout?.createdAt as string),
            //   "PPpp"
            // ),
          },
        },
      };
    });

    const holidays = authData?.user?.holidayList?.holidays;

    // @ts-ignore
    const holidaysMarking = holidays?.map((holiday) => {
      const from = moment(holiday?.date)?.toDate();
      const to = moment(holiday?.date)?.toDate();

      return {
        start: from,
        end: to,
        data: {
          holiday: {
            status: holiday?.holidayName,
            date: holiday?.date,
          },
        },
      };
    });

    return [...result, ...holidaysMarking];
  }, [data, isPending]);

  console.log("ATTENDANCE-EVENTS", data);

  const detailingFilterEvents = useMemo(() => {
    const seenStartDates = new Set<string>();

    return ATTENDANCE_EVENTS.filter((item) => {
      const startDate = new Date(item.start);
      const yearMonthStr = `${startDate.getFullYear()}-${String(
        startDate.getMonth() + 1
      ).padStart(2, "0")}-${startDate.getDate()}`;

      if (
        startDate.getFullYear() === selectedYear &&
        startDate.getMonth() === selectedMonth
      ) {
        if (!seenStartDates.has(yearMonthStr)) {
          seenStartDates.add(yearMonthStr);
          return true;
        }
      }
      return false;
    });
  }, [ATTENDANCE_EVENTS, selectedMonth, selectedYear]);

  const details = useMemo(() => {
    if (detailingFilterEvents?.length === 0) {
      return null;
    }

    const endResult: {
      [key: string]: string[];
    } = {
      Present: [],
      Absent: [],
      "Paid Leave": [],
      "On Leave": [],
      Holiday: [],
      "Not Counting": [],
    };

    detailingFilterEvents?.forEach((att) => {
      const key = att?.data?.attendance?.status;
      endResult[key]?.push(att?.data?.attendance?.date);
    });

    return endResult;
  }, [detailingFilterEvents]);

  const components: any = {
    event: ({ event }: EventProps<AttendanceEventItem>) => {
      const data = event?.data;
      if (data?.attendance)
        return (
          <AttendanceEvent attendance={data?.attendance} />
          // <AppointmentEvent
          //   appointment={data?.appointment}
          //   isMonthView={view === Views.MONTH}
          // />
        );

      if (data?.holiday) {
        return <HolidayEvent holiday={data?.holiday} />;
      }

      // if (data?.blockout) {
      //   return <BlockoutEvent blockout={data?.blockout} />;
      // }

      return null;
    },
  };

  const STEP = 5;
  const TIME_SLOTS = 60 / STEP;

  const totalDays = useMemo(() => {
    const date = new Date(selectedYear, selectedMonth + 1, 1);
    date.setDate(date.getDate() - 1);

    return date.getDate();
  }, [selectedYear, selectedMonth]);

  const { mutate: CreateDateSheetClaim, isPending: isClaiming } = useMutation({
    mutationFn: createDateSheetClaim(key),
  });

  const [isOpen, setIsOpen] = useState(false);

  const onClaiming = () => {
    if (details === null) return;

    if (details["Not Counting"]?.length > 0) {
      return toast(
        "You have one or more days with Not Counting Status. Either Mark absent yourself or request for attendance."
      );
    }

    CreateDateSheetClaim(
      {
        monthName: months?.find((t) => t.value === selectedMonth)
          ?.label as string,
        absent: details["Absent"]?.length,
        present: details["Present"]?.length,
        paidLeave: details["Paid Leave"]?.length,
        unpaidLeave: details["On Leave"]?.length,
        year: selectedYear,
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: ["timesheets-month"],
          });
          setIsOpen(false);
          toast.success("Salary Timesheet Claimed");
        },
        onError: (error) => {
          console.log("ERROR WHILE CLAIMING TIMESHEET", error);
          const isAxiosError = error instanceof AxiosError;
          if (isAxiosError) {
            const errorReason = error?.response?.data?.error as string;
            toast.error(errorReason);
          } else {
            toast.error(error?.message);
          }
        },
      }
    );
  };

  return (
    <>
      <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
        <AlertDialogTrigger asChild>
          <Button size="sm">Claim Timesheet</Button>
        </AlertDialogTrigger>
        <AlertDialogContent className="max-w-[95%]">
          <AlertDialogHeader>
            <AlertDialogTitle className="tracking-wide">
              Claim Salary Timesheet
            </AlertDialogTitle>
          </AlertDialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="Month">Month</Label>
              <Select
                value={String(selectedMonth)}
                onValueChange={(value) => {
                  setSelectedMonth(Number(value));
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a month" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  <SelectGroup>
                    {months.map((month) => {
                      return (
                        <SelectItem
                          key={month.value}
                          value={String(month.value)}
                          disabled={month.disabled}
                        >
                          {month.label}
                        </SelectItem>
                      );
                    })}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col md:flex-row gap-3 items-center">
              <div className="flex-1">
                <Label htmlFor="Year">Year</Label>
                <Select
                  value={String(selectedYear)}
                  onValueChange={(value) => {
                    setSelectedYear(Number(value));
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a year" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    <SelectGroup>
                      {years.map((year) => {
                        return (
                          <SelectItem key={year} value={String(year)}>
                            {year}
                          </SelectItem>
                        );
                      })}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>

              <Button
                className="self-end"
                onClick={() => {
                  setDate(getMiddleDayOfMonth(selectedYear, selectedMonth));
                  const month = months.find((m) => m.value === selectedMonth);
                  mutate({
                    monthName: month?.label as string,
                    year: selectedYear,
                  });
                }}
              >
                See
              </Button>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="h-[70vh] w-[70vw]">
              <Calendar
                events={ATTENDANCE_EVENTS}
                defaultView={"month"}
                // resources={view === Views.DAY ? RESOURCES : undefined}
                components={components}
                toolbar={false}
                date={date}
                view={"month"}
                // onView={setView}
                step={STEP}
                timeslots={TIME_SLOTS}
              />
            </div>

            {details !== null && (
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-3">
                  <span>Total Days - {totalDays}</span>
                </div>
                <Separator />
                <div className="flex items-center gap-3">
                  <span>Present - {details["Present"]?.length}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span>Absent - {details["Absent"]?.length}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span>Paid Leave - {details["Paid Leave"]?.length}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span>Unpaid Leave - {details["On Leave"]?.length}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span>Not Counting - {details["Not Counting"]?.length}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span>Holiday - {details["Holiday"]?.length}</span>
                </div>
              </div>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isClaiming}>Cancel</AlertDialogCancel>
            <Button onClick={onClaiming} disabled={isClaiming}>
              Continue
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
